import { Flex } from '@chakra-ui/react';
import { MAX_FONT_SIZE, MIN_FONT_SIZE } from 'constants/fonts';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { EDITOR_NAVBAR_ID, EDITOR_TOOLBAR_ID, WIDGET_MENU_ID } from 'modules/Editor/Editor.config';
import { PageNumberMenu } from 'modules/Editor/components/Toolbar/PageMenu/PageNumberMenu';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { setIsPageNumberToolbarOpen } from 'modules/Editor/store/editorSettingsSlice';
import { selectHasPageNumber, selectPageNumberStyle } from 'modules/Editor/store/infographSelector';
import { setPageNumberStyle } from 'modules/Editor/store/infographSlice';
import { FontFamilySelect } from 'modules/common/components/FontFamilySelect';
import { Bold, Italic, Underline } from 'modules/common/components/FontStyle';
import { ToolbarDivider } from 'modules/common/components/Toolbar/ToolbarDivider';
import { ReactElement, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontSize, TextColor } from 'widgets/TextBasedWidgets/common/toolbar';

const FONT_FAMILY_BUTTON_STYLE = {
  size: 'xs',
  fontSize: 'xs',
  height: 8,
};

export const PageNumberToolbar = (): ReactElement => {
  const dispatch = useAppDispatch();
  const pageNumberToolbarRef = useRef<HTMLDivElement>(null);
  const pageNumberStyle = useAppSelector(selectPageNumberStyle);
  const isPageNumberEnabled = useAppSelector(selectHasPageNumber);
  const { color: fontColor, fontFamily, fontSize, fontWeight, fontStyle, textDecoration } = pageNumberStyle;

  const { t } = useTranslation('common_navbar');
  const settingsLabel = t('pageNumber.settings');

  const onFontColorChange = useCallback(
    (color: string) => {
      dispatch(setPageNumberStyle({ color }));
    },
    [dispatch],
  );

  const onFontFamilyChange = useCallback(
    (fontFamily: string) => {
      dispatch(setPageNumberStyle({ fontFamily }));
    },
    [dispatch],
  );

  const onFontSizeChange = useCallback(
    (fontSize: number) => {
      if (!fontSize) return;
      dispatch(setPageNumberStyle({ fontSize }));
    },
    [dispatch],
  );

  const onFontSizeIncrement = useCallback(() => {
    if (fontSize >= MAX_FONT_SIZE) return;
    dispatch(setPageNumberStyle({ fontSize: fontSize + 1 }));
  }, [dispatch, fontSize]);

  const onFontSizeDecrement = useCallback(() => {
    if (fontSize <= MIN_FONT_SIZE) return;
    dispatch(setPageNumberStyle({ fontSize: fontSize - 1 }));
  }, [dispatch, fontSize]);

  const onFontWeightChange = useCallback(() => {
    dispatch(setPageNumberStyle({ fontWeight: fontWeight === 'bold' ? 'normal' : 'bold' }));
  }, [dispatch, fontWeight]);

  const onFontStyleChange = useCallback(() => {
    dispatch(setPageNumberStyle({ fontStyle: fontStyle === 'italic' ? 'normal' : 'italic' }));
  }, [dispatch, fontStyle]);

  const onTextDecorationChange = useCallback(() => {
    dispatch(setPageNumberStyle({ textDecoration: textDecoration === 'underline' ? 'none' : 'underline' }));
  }, [dispatch, textDecoration]);

  // when page number is disabled, closes the toolbar when clicking outside page number toolbar (except for the editor menu / toolbars)
  useOutsideClick({
    ref: pageNumberToolbarRef,
    handler: () => {
      // ignore if page number is enabled
      if (isPageNumberEnabled) return;
      dispatch(setIsPageNumberToolbarOpen(false));
    },
    ignoreId: [EDITOR_NAVBAR_ID, EDITOR_TOOLBAR_ID, WIDGET_MENU_ID],
  });

  return (
    <Flex mr={2} gap={2} align='center' ref={pageNumberToolbarRef}>
      <TextColor color={fontColor} onChange={onFontColorChange} showNoColorChecker={true} showBrandSection={false} />
      <FontFamilySelect
        fontFamily={fontFamily}
        onChange={onFontFamilyChange}
        width='9rem'
        borderRadius='base'
        buttonProps={FONT_FAMILY_BUTTON_STYLE}
      />
      <FontSize
        value={fontSize}
        onChange={onFontSizeChange}
        onDecrement={onFontSizeDecrement}
        onIncrement={onFontSizeIncrement}
      />
      <ToolbarDivider />
      <Bold fontFamily={fontFamily} isActive={fontWeight === 'bold'} onChange={onFontWeightChange} />
      <Italic fontFamily={fontFamily} isActive={fontStyle === 'italic'} onChange={onFontStyleChange} />
      <Underline isActive={textDecoration === 'underline'} onChange={onTextDecorationChange} />
      <ToolbarDivider />
      <PageNumberMenu label={settingsLabel} />
    </Flex>
  );
};

PageNumberToolbar.displayName = 'PageNumberToolbar';
